// src/native/ui/nativeLinks.jsx
//
// Zero Safari hops.
//
// Two separate problems, one listener:
//
// 1. INTERNAL links that open in a new tab are DEAD in a native webview.
//    `<a href="/privacy" target="_blank">` works fine in a browser, but WKWebView
//    has no tabs, so the tap does nothing at all. Those exact links are how a
//    reviewer reaches Privacy and Terms from the paywall (Pricing.jsx:868-869,
//    ConsentBanner.jsx:126, FbOnboarding screens.jsx:637-638,
//    FbPricing/index.jsx:496). A dead legal link on the purchase screen is a
//    rejection under 3.1.2 and feeds the 4.2 "this is a website" read. Here they
//    are turned into ordinary in-app route changes.
//
// 2. EXTERNAL links would eject the user into Safari. Apple's 4.2 posture is
//    that the app's value must not visibly live in a browser, and a March 2026
//    case suggests even policy pages rendered as web content can trigger it.
//    External links are therefore opened in SFSafariViewController via
//    @capacitor/browser, which presents over the app and returns to it, instead
//    of switching to the Safari app.
//
// The listener is installed only when isNative() is true. In a browser this
// module registers nothing, and the @capacitor/browser chunk is never requested
// because the import lives inside loadNative().

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNative, loadNative } from '@/lib/platform';

// Anything with a file extension is a real asset served by the local server
// (/logoofapp.png, /offline.html, /llms.txt), not an SPA route. Let the webview
// handle those normally instead of feeding them to the router as a 404.
const ASSET_PATH = /\.[a-z0-9]{2,5}$/i;

// Payment hosts must never be opened from the native app. In-app unlocks go
// through StoreKit (WS-3); sending a native user to a Stripe page is an
// automatic 3.1.1 rejection. If WS-3 has done its job this never fires, so it
// is a tripwire rather than a feature.
const BLOCKED_NATIVE_HOSTS = [
  'checkout.stripe.com',
  'buy.stripe.com',
  'billing.stripe.com',
];

/**
 * Open a URL from native code without leaving the app.
 * Resolves to false on the web (and on any failure) so callers can fall through
 * to whatever they did before.
 *
 * @param {string} url absolute http(s) URL
 * @returns {Promise<boolean>} true when the in-app browser actually opened it
 */
export async function openExternalUrl(url) {
  if (!isNative() || !url) return false;
  try {
    const parsed = new URL(url, window.location.href);
    if (BLOCKED_NATIVE_HOSTS.includes(parsed.hostname)) {
      console.error('[nativeLinks] refused to open a payment host natively:', parsed.hostname);
      return false;
    }
    const mod = await loadNative(() => import('@capacitor/browser'));
    if (!mod?.Browser?.open) return false;
    await mod.Browser.open({ url: parsed.href, presentationStyle: 'popover' });
    return true;
  } catch (err) {
    console.warn('[nativeLinks] in-app browser open failed:', err);
    return false;
  }
}

/**
 * Install the document-level anchor interceptor.
 *
 * @param {(path: string) => void} navigate router navigate function
 * @returns {() => void} uninstall function. A no-op on the web.
 */
export function installNativeLinkInterceptor(navigate) {
  if (!isNative() || typeof document === 'undefined') return () => {};

  const onClick = (event) => {
    // Anything the app already handled stays handled.
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) return;
    if (anchor.hasAttribute('download')) return;

    const raw = anchor.getAttribute('href') || '';
    if (!raw || raw.startsWith('#')) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }

    // mailto:, tel:, sms:, itms-apps: and friends are handed to the system by
    // the webview and open the right app. Leave them alone.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

    if (url.origin === window.location.origin) {
      // Same origin: an in-app route. Only worth intercepting when the default
      // would misbehave, which is any target that is not the current frame, and
      // any full page reload we can turn into a route change.
      if (ASSET_PATH.test(url.pathname)) return;
      event.preventDefault();
      navigate(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    // Cross origin: present in-app rather than switching to Safari.
    event.preventDefault();
    void openExternalUrl(url.href);
  };

  document.addEventListener('click', onClick);
  return () => document.removeEventListener('click', onClick);
}

/**
 * Mounts the interceptor. Renders nothing. Must live inside the router, which
 * is where App.jsx mounts NativeAppShell.
 */
export const NativeLinkGuard = () => {
  const navigate = useNavigate();

  useEffect(() => installNativeLinkInterceptor(navigate), [navigate]);

  return null;
};

export default NativeLinkGuard;
